// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';
import {
  UserWorkspace,
  WorkspaceDataRoom,
  WorkspaceType,
} from '@/apis/core/workspace/get/getUserWorkspace';
import { AskSession } from '@/apis/ask/askHistory/types';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_CORE_URL;
const API_ENDPOINT = '/api/Workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_CORE_SCOPE;

const HEADERS = {
  'Content-Type': 'application/json',
};

// ============= Types =============

export interface PostCreateWorkspaceRequestBody {
  title: string;
  description: string;
  workspaceUsers: Array<{ userId: string; isOwner: boolean }>;
  matterUno?: string;
  clientId?: string;
}

/**
 * Raw API response shape from the create workspace endpoint.
 * Mapped to UserWorkspace before returning so callers use a single canonical type.
 */
interface CreateWorkspaceApiResponse {
  id: string;
  _etag: string;
  title: string;
  description: string;
  createdOn: string;
  matterCode?: string;
  matterUno?: string;
  matterName?: string;
  clientName?: string;
  clientUno?: string;
  clientCode?: string;
  creatorId: string;
  workspaceType: string;
  askSessions: AskSession[];
  workflows: unknown[];
  dataRoom: WorkspaceDataRoom;
  workspaceUsers: Record<string, boolean>;
  lastUpdate: string;
  markedForDeletion: string | null;
  deletionProcessStarted: string | null;
}

export interface PostCreateWorkspaceProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  body?: PostCreateWorkspaceRequestBody;
}

/**
 * Maps the create workspace API response to the canonical UserWorkspace type.
 * Ensures create and get workspace responses share the same shape for remapping via TransformedUserWorkspace.
 */
function mapCreateResponseToUserWorkspace(
  data: CreateWorkspaceApiResponse
): UserWorkspace {
  return {
    id: data.id,
    _etag: data._etag,
    title: data.title,
    description: data.description ?? '',
    createdOn: data.createdOn,
    matterNumber: data.matterCode ?? data.matterUno ?? null,
    matterName: data.matterName ?? null,
    matterCode: data.matterCode ?? null,
    clientName: data.clientName ?? null,
    clientId: data.clientCode ?? data.clientUno ?? null,
    clientCode: data.clientCode ?? null,
    creatorId: data.creatorId,
    workspaceType: data.workspaceType as WorkspaceType,
    askSessions: data.askSessions ?? [],
    workflows: data.workflows ?? [],
    dataRoom: data.dataRoom,
    workspaceUsers: data.workspaceUsers ?? {},
    lastUpdate: data.lastUpdate,
    markedForDeletion: data.markedForDeletion ?? null,
    deletionProcessStarted: data.deletionProcessStarted ?? null,
  };
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Creates a new workspace.
 * Returns the canonical UserWorkspace type so callers can remap via transformAllUserWorkspaces to TransformedUserWorkspace when needed.
 *
 * @param getAccessToken Function to acquire access token
 * @param body Optional request body
 * @returns Promise with UserWorkspace (mapped from API response) or error
 */
export const postCreateWorkspace = async ({
  getAccessToken,
  body,
}: PostCreateWorkspaceProps): Promise<UserWorkspace> => {
  try {
    console.log('🔄 Creating workspace...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/create`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'POST',
      headers: createHeaders(token),
      body: body ? JSON.stringify(body) : undefined,
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: CreateWorkspaceApiResponse = await response.json();
    console.log(`✅ Workspace created successfully: ${data}`);

    return mapCreateResponseToUserWorkspace(data);
  } catch (error) {
    console.error('🔴 Error creating workspace:', error);
    throw error;
  }
};
